import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import SocialIcons from './SocialIcons';

interface HeaderProps {
    highlightColor: {
        primary: string;
        hover: string;
        gradient: {
            from: string;
            to: string;
            fromLight: string;
            toGray: string;
        };
    };
}

const LandingHeader: React.FC<HeaderProps> = ({ highlightColor }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const [headerVisible, setHeaderVisible] = useState(true);
    const [lastScrollY, setLastScrollY] = useState(0);
    const [menuOpen, setMenuOpen] = useState(false);
    const headerRef = React.useRef<HTMLDivElement>(null);

    const menuItems = [
        { label: 'Home', href: '/' },
        { label: 'About', href: '/about' },
        { label: 'Services', href: '/services' },
        { label: 'Book a Demo', href: '/pricing' },
        { label: 'Blog', href: '/blog' },
        { label: 'Contact', href: '/contact' },
    ];

    // Handle header visibility on scroll
    useEffect(() => {
        const handleScroll = () => {
            const currentScrollY = window.scrollY;

            if (currentScrollY < 10) {
                setHeaderVisible(true);
            } else if (currentScrollY > lastScrollY) {
                setHeaderVisible(false);
            } else {
                setHeaderVisible(true);
            }

            setLastScrollY(currentScrollY);
        };

        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, [lastScrollY]);

    // Handle click outside to close menu
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (menuOpen && headerRef.current && !headerRef.current.contains(event.target as Node)) {
                setMenuOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [menuOpen]);

    // Close menu when location changes
    useEffect(() => {
        setMenuOpen(false);
    }, [location.pathname]);

    const isActive = (href: string) => {
        if (href === '/') return location.pathname === '/';
        return location.pathname.startsWith(href);
    };

    return (
        <>
            {/* Border at top edge - always visible when header is hidden */}
            <div 
                className={`fixed top-0 left-0 right-0 h-px z-50 transition-opacity duration-300  ${
                    headerVisible ? 'opacity-0' : 'opacity-100'
                }`}
                style={{ backgroundColor: highlightColor.hover }}
            >
                <div className="laser-beam"></div>
            </div>
            
            {/* Navigation */}
            <nav 
                ref={headerRef}
                className={`fixed top-0 left-0 right-0 z-40 bg-black/40 backdrop-blur-xl transition-all duration-300 ease-in-out ${
                    headerVisible ? 'translate-y-0' : '-translate-y-full'
                }`}
            >
                {/* Bottom border - visible when header is shown */}
                <div className="absolute bottom-0 left-0 right-0 h-px" style={{ backgroundColor: highlightColor.hover }}>
                    <div className="laser-beam"></div>
                </div>
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    {/* Top row with logo and hamburger */}
                    <div className="flex justify-between items-center h-16">
                        <div className="flex items-center -ml-14">
                            <img
                                src="/logos/LOGO-MAIN-WHITE.svg"
                                alt="CGM Sports"
                                className="h-12 w-auto cursor-pointer transition-transform hover:scale-105"
                                onClick={() => navigate('/')}
                                onError={(e) => {
                                    (e.target as HTMLImageElement).src = '/logos/LOGO-MAIN-WHITE.png';
                                }}
                            />
                        </div>
                        <div className="flex items-center gap-3">
                            <button
                                onClick={() => navigate('/login')}
                                className="text-sm font-medium text-gray-300 hover:text-white transition-colors cursor-pointer"
                            >
                                Log In
                            </button>
                            <button
                                onClick={() => navigate('/register')}
                                className="text-sm font-medium px-4 py-1.5 bg-white text-black hover:bg-gray-200 transition-colors cursor-pointer"
                            >
                                Sign Up
                            </button>
                            <button
                                onClick={() => setMenuOpen(!menuOpen)}
                                className="relative flex items-center justify-center cursor-pointer overflow-visible transition-all duration-300 min-w-fit min-h-fit"
                                style={{
                                    padding: menuOpen ? '0.75rem' : '0.5rem'
                                }}
                                aria-label="Navigation menu"
                            >
                                <div className={`relative overflow-visible transition-all duration-300 ${
                                    menuOpen ? 'w-7 h-7' : 'w-6 h-6'
                                }`}>
                                    <span
                                        className={`absolute left-0 w-full h-0.5 bg-white transition-all duration-300 origin-center ${
                                            menuOpen ? 'rotate-45 top-1/2 -translate-y-1/2' : 'top-0'
                                        }`}
                                    />
                                    <span
                                        className={`absolute top-1/2 left-0 w-full h-0.5 bg-white transition-all duration-300 -translate-y-1/2 ${
                                            menuOpen ? 'opacity-0' : 'opacity-100'
                                        }`}
                                    />
                                    <span
                                        className={`absolute left-0 w-full h-0.5 bg-white transition-all duration-300 origin-center ${
                                            menuOpen ? '-rotate-45 top-1/2 -translate-y-1/2' : 'bottom-0'
                                        }`}
                                    />
                                </div>
                            </button>
                        </div>
                    </div>

                    {/* Menu items container - expands vertically */}
                    <div 
                        className={`transition-all duration-300 ease-in-out overflow-hidden ${
                            menuOpen ? 'max-h-96 opacity-100 pointer-events-auto' : 'max-h-0 opacity-0 pointer-events-none'
                        }`}
                    >
                        <div className="pb-4 pt-2 flex flex-col space-y-4">
                            {menuItems.map((item, index) => (
                                <Link
                                    key={index}
                                    to={item.href}
                                    onClick={() => setMenuOpen(false)}
                                    className={`text-base font-medium transition-all duration-200 relative py-2 ${
                                        isActive(item.href)
                                            ? 'text-white'
                                            : 'text-gray-300 hover:text-white'
                                    }`}
                                    style={{
                                        ...(isActive(item.href) && {
                                            color: highlightColor.hover
                                        })
                                    }}
                                    onMouseEnter={(e) => {
                                        if (!isActive(item.href)) {
                                            e.currentTarget.style.color = highlightColor.hover;
                                        }
                                    }}
                                    onMouseLeave={(e) => {
                                        if (!isActive(item.href)) {
                                            e.currentTarget.style.color = '';
                                        }
                                    }}
                                >
                                    {item.label}
                                    {isActive(item.href) && (
                                        <span 
                                            className="absolute left-0 bottom-0 w-full h-0.5"
                                            style={{ backgroundColor: highlightColor.hover }}
                                        />
                                    )}
                                </Link>
                            ))}
                            
                            {/* Social Icons */}
                            <div>
                                <SocialIcons size="sm" />
                            </div>
                        </div>
                    </div>
                </div>
            </nav>
        </>
    );
};

export default LandingHeader;
